import React, { FC } from 'react'
import { useFormContext } from 'react-hook-form'
import { ISelectOption } from 'components/forms/Selects'
import camelCase from 'lodash/camelCase'
import moment from 'moment'
import { DeprecatedControlledSelect } from 'transfix-form/components/ControlledSelect'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import PeopleIcon from 'transfix-icons/build/PeopleIcon'
import { TrackingTag } from 'transfix-ui/components/TrackingTag'
import { Box } from 'transfix-ui/core'
import { ITrackingStatus, TTrackingMethod, TTrackingState } from 'types'
import { Shipment } from 'apps/carrier/apiTypes/activeShipments'
import ShipmentStatusIcons from 'apps/marketplace/components/ShipmentStatusIcons'
import { EQUIPMENT_TYPE_KEY } from 'apps/marketplace/store/transfixLane/types'
import { getLargeEquipmentIcon } from 'apps/marketplace/utils/helpers/getIcon'
import { Tag, TAG_SIZE } from 'transfix-ui'
import DriverPhoneRender from './DriverPhoneRender'

interface IShipmentFieldsProps {
  shipment: Shipment
  driverList: ISelectOption<number>[]
}

interface IMissingShipmentFields {
  vin?: boolean
  trailerNumber?: boolean
  truckNumber?: boolean
}

const ShipmentFields: FC<IShipmentFieldsProps> = ({ driverList, shipment }) => {
  const {
    formState: { isSubmitSuccessful },
  } = useFormContext()

  const { errors } = useFormContext()

  // Transforms array of missing shipment field keys to object set to 'true' value
  // in order to highlight its corresponding input
  const missingFields = shipment.missingFields?.reduce((acc: IMissingShipmentFields, curr) => {
    acc[camelCase(curr) as keyof IMissingShipmentFields] = true
    return acc
  }, {})

  const { shipperTrackingSegment: tracking, trackingStatus } = shipment

  const shipperTracking = {
    title: `Level ${tracking?.level}: ${tracking?.description}`,
    id: String(tracking?.id),
    description: tracking?.description || '',
    level: tracking?.level || 0,
    minReqTrackingPercentage: tracking?.minReqTrackingPercentage || 0,
  }

  const shipmentTrackingStatus: ITrackingStatus = {
    trackingState: trackingStatus?.trackingState as TTrackingState,
    trackingMethod: trackingStatus?.trackingMethod as TTrackingMethod,
  }

  const filterDropTrailerStops = [shipment.pickups, shipment.deliveries]
    .flat()
    .filter(stop => stop?.isDropTrailer)

  const shipmentIcons = {
    dropTrailer: filterDropTrailerStops.length > 0,
    dropTrailerStops: filterDropTrailerStops.length,
    pricedUnderContract: shipment?.pricedUnderContract,
    hot: shipment?.hot,
    rolloverCount: shipment?.rolloverCount,
    bouncedCount: shipment?.bouncedCount || 0,
    hasHazmat: shipment?.hasHazmat,
    hasBackhaulEligibleLane: shipment?.hasBackhaulEligibleLane,
  }

  return (
    <Box display='flex' flexDirection='column' pr={3} width='30%'>
      <Box mb={2}>
        {shipment.shipperApplication?.name?.substr(0, 3).toUpperCase()}{' '}
        <a
          href={`${__ROOT__}/shipment-details/${shipment.id}?tab=scheduling`}
          target='_blank'
          rel='noopener noreferrer'
          className='mr1'>
          #{shipment.id}
        </a>
        {isSubmitSuccessful && (
          <Box component='span' ml={1} className='text-caption-semibold'>
            Last updated {moment().fromNow()}
          </Box>
        )}
      </Box>
      <Box display='flex' mb={2} justifyContent='space-between' alignItems='center'>
        <Box display='flex' flexWrap='wrap'>
          <Box mr={1}>{getLargeEquipmentIcon(shipment.equipment?.icon as EQUIPMENT_TYPE_KEY)}</Box>
          {shipment.team && (
            <Box marginX={1}>
              <PeopleIcon />
            </Box>
          )}
          <ShipmentStatusIcons status={shipmentIcons} />
          <Box display='flex' flexWrap='nowrap'>
            <Box>
              <TrackingTag
                shipperTrackingSegment={shipperTracking}
                trackingStatus={shipmentTrackingStatus}
              />
            </Box>
            <Tag label={shipment.status?.state || ''} size={TAG_SIZE.xsmall} />
          </Box>
        </Box>
      </Box>
      <Box display='flex' flexDirection='column'>
        <Box display='flex' mb={1}>
          <Box width='40%' mr={2}>
            <DeprecatedControlledSelect
              name='driverId'
              SelectProps={{
                id: `driver-select-${shipment.id}`,
                label: 'Driver',
                ariaLabel: 'Driver',
                options: driverList,
              }}
            />
          </Box>
          <Box width='60%' display='flex'>
            <DeprecatedControlledTextInput
              name='truckNumber'
              TextInputProps={{
                highlighted: missingFields?.truckNumber ? 'warning' : undefined,
                clearable: false,
                label: 'Truck #',
                className: 'mr3',
                dataTest: 'truck-number',
              }}
            />
            <DeprecatedControlledTextInput
              name='trailerNumber'
              TextInputProps={{
                highlighted: missingFields?.trailerNumber ? 'warning' : undefined,
                label: 'Trailer #',
                className: 'mr3',
                clearable: false,
                dataTest: 'trailer-number',
              }}
            />
            <DeprecatedControlledTextInput
              name='vin'
              TextInputProps={{
                highlighted: missingFields?.vin ? 'warning' : undefined,
                label: 'VIN #',
                clearable: false,
                dataTest: 'vin-number',
                invalid: errors?.vin?.message,
              }}
            />
          </Box>
        </Box>
        <Box display='flex' alignItems='center'>
          <DriverPhoneRender phone={shipment.driver?.phone} driverId={shipment.driver?.id} />
        </Box>
      </Box>
    </Box>
  )
}

export default ShipmentFields
